import { create, StoreApi } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createFileLogger } from '../../../utils/logger';
import {
  ImageStudioState,
  ImageStudioProject,
  Layer,
  LayerType,
  LayerTransform,
  AIOperation,
  AIOperationMode,
  ApplyMode,
  Selection,
  SelectionMode,
  SelectionType,
  HistoryEntry,
  StudioTool,
  ViewTransform,
  BrushSettings,
  StyleRecipe,
  VariantResult,
  DEFAULT_PROJECT,
  DEFAULT_BRUSH_SETTINGS,
  DEFAULT_VIEW_TRANSFORM,
  DEFAULT_SELECTION,
  AdjustmentFilters,
} from './types';

const log = createFileLogger('IMAGE_STUDIO', { enabled: true });

// Per-window store instances
const imageStudioStores = new Map<string, ReturnType<typeof createImageStudioStore>>();

// ─── Full store interface ───────────────────────────────────────────────────

export interface ImageStudioStore extends ImageStudioState {
  // Lifecycle
  initialize: (sourceImageUrl?: string, sourceImageName?: string, sourceEntityId?: string) => Promise<void>;
  cleanup: () => void;

  // ── Layer actions ──
  addLayer: (type: LayerType, props?: Partial<Layer>) => string;
  removeLayer: (id: string) => void;
  removeSelectedLayers: () => void;
  duplicateLayer: (id: string) => string;
  reorderLayers: (fromIndex: number, toIndex: number) => void;
  setLayerVisibility: (id: string, visible: boolean) => void;
  setLayerLocked: (id: string, locked: boolean) => void;
  setLayerOpacity: (id: string, opacity: number) => void;
  setLayerBlendMode: (id: string, blendMode: Layer['blendMode']) => void;
  setLayerTransform: (id: string, transform: Partial<LayerTransform>) => void;
  setLayerName: (id: string, name: string) => void;
  setLayerFilters: (id: string, filters: AdjustmentFilters) => void;
  replaceLayerContent: (id: string, imageData: string, width: number, height: number, preserveTransform: boolean) => void;
  setSelectedLayers: (ids: string[]) => void;
  toggleLayerSelection: (id: string) => void;
  alignLayers: (ids: string[], anchor: 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom', relativeTo: 'canvas' | 'selection') => void;
  distributeLayers: (ids: string[], axis: 'horizontal' | 'vertical') => void;
  mergeLayersDown: (id: string) => void;

  // ── Selection actions ──
  setSelection: (type: SelectionType, bounds: Selection['bounds']) => void;
  setSelectionMode: (mode: SelectionMode) => void;
  clearSelection: () => void;
  invertSelection: () => void;
  selectAll: () => void;
  bumpMaskSerial: () => void;
  setActiveMaskData: (data: string | null) => void;

  // ── Agent result reception ──
  /** Register a pending AI operation kicked off by the agent (tool start).
   *  Shows a placeholder card in the VariantsStrip while DALL-E runs. */
  receiveAgentProgress: (opId: string, prompt: string, mode?: AIOperationMode) => void;
  /** Called when an AI agent tool (image_edit_region etc.) finishes and
   *  pushes its result back to the browser. Shows result in VariantsStrip.
   *  imageUrls is an array of fetchable image URLs (typically
   *  /api/files/read/{entityId}?token=...) produced by persist-variant. */
  receiveAgentEditResult: (opId: string | null, variants: Array<{ imageUrl: string; entityId?: string; name?: string }>, prompt: string, mode?: AIOperationMode) => void;
  /** Mark a pending agent operation as failed — clears the spinner. */
  receiveAgentFailure: (opId: string, error: string) => void;

  // ── Canvas / zoom actions ──
  setViewTransform: (t: Partial<ViewTransform>) => void;
  fitToScreen: (containerWidth: number, containerHeight: number) => void;
  zoomTo100: () => void;
  zoomToSelection: (containerWidth: number, containerHeight: number) => void;
  setCanvasSize: (width: number, height: number) => void;
  setCanvasBackground: (background: string | null) => void;

  // ── Tool / brush actions ──
  setActiveTool: (tool: StudioTool) => void;
  setBrushSettings: (settings: Partial<BrushSettings>) => void;

  // ── AI operation actions ──
  createAIOperation: (mode: AIOperationMode, prompt: string, opts?: Partial<AIOperation>) => AIOperation;
  updateAIOperation: (id: string, updates: Partial<AIOperation>) => void;
  receiveAIResults: (opId: string, variants: AIOperation['resultVariants']) => void;
  cancelAIOperation: (opId: string) => void;
  acceptAIVariant: (opId: string, variantIndex: number, applyMode: ApplyMode) => string | null;
  rejectAIVariants: (opId: string) => void;
  setActiveVariantsOp: (opId: string | null) => void;
  /** Reopen the variants strip on the most recent completed agent/user op.
   *  Returns true if there was a completed op to show, false otherwise. */
  reopenVariantsStrip: () => boolean;
  /** Move activeVariantsOpId to the previous/next completed operation
   *  (ordered by createdAt). No-op at either end. */
  stepVariantsOp: (direction: 'prev' | 'next') => void;

  // ── History ──
  pushHistory: (entry: Omit<HistoryEntry, 'id' | 'timestamp' | 'branchId'>) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // ── Style recipes ──
  saveStyleRecipe: (recipe: Omit<StyleRecipe, 'id'>) => void;
  deleteStyleRecipe: (id: string) => void;

  // ── UI panel toggles ──
  setShowLayerPanel: (show: boolean) => void;
  setShowInspector: (show: boolean) => void;
  setShowHistoryPanel: (show: boolean) => void;

  // ── Persistence ──
  setIsSaving: (saving: boolean) => void;
  updateProjectMeta: (updates: Partial<Pick<ImageStudioProject, 'title' | 'updatedAt'>>) => void;
}

// ─── Factory ───────────────────────────────────────────────────────────────

function generateId(prefix = 'id'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function autoLayerName(layers: Layer[], type: LayerType): string {
  const sametype = layers.filter(l => l.type === type).length;
  const label = type.charAt(0).toUpperCase() + type.slice(1);
  return `${label} ${sametype + 1}`;
}

const createImageStudioStore = (windowId: string, spaceId: string) => {
  return create<ImageStudioStore>()(
    subscribeWithSelector(
      immer<ImageStudioStore>((set, get) => ({
        // ── Initial state ──────────────────────────────────────────────────
        project: DEFAULT_PROJECT(),
        selectedLayerIds: [],
        activeTool: 'select',
        selection: DEFAULT_SELECTION(),
        brushSettings: DEFAULT_BRUSH_SETTINGS,
        viewTransform: DEFAULT_VIEW_TRANSFORM,
        activeMaskData: null,
        aiOperations: [],
        activeOperationId: null,
        historyStack: [],
        historyIndex: -1,
        currentBranchId: 'main',
        showLayerPanel: true,
        showInspector: true,
        showHistoryPanel: false,
        showVariantsStrip: false,
        activeVariantsOpId: null,
        latestExportUrl: null,
        isInitialized: false,
        isSaving: false,

        // ── Lifecycle ──────────────────────────────────────────────────────
        initialize: async (sourceImageUrl?: string, sourceImageName?: string, sourceEntityId?: string) => {
          log.log('[imageStudioStore] initialize', { windowId, spaceId, hasSourceImage: !!sourceImageUrl, sourceEntityId });

          if (sourceImageUrl) {
            // Load the image to get its natural dimensions before initializing
            const naturalSize = await new Promise<{ width: number; height: number }>((resolve) => {
              const img = new Image();
              img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
              img.onerror = () => resolve({ width: 1024, height: 1024 });
              img.src = sourceImageUrl;
            });

            const layerId = generateId('layer');
            const project = DEFAULT_PROJECT();
            project.id = generateId('project');
            project.title = sourceImageName || 'Image Studio';
            project.canvas.width = naturalSize.width;
            project.canvas.height = naturalSize.height;

            const layer: Layer = {
              id: layerId,
              type: 'image',
              name: sourceImageName || 'Background',
              visible: true,
              locked: false,
              opacity: 1,
              blendMode: 'normal',
              transform: { x: 0, y: 0, width: naturalSize.width, height: naturalSize.height, rotation: 0 },
              imageData: sourceImageUrl,
            };

            project.layers = [layer];

            set(state => {
              state.project = project;
              state.selectedLayerIds = [layerId];
              state.isInitialized = true;
            });
          } else {
            const project = DEFAULT_PROJECT();
            project.id = generateId('project');
            set(state => {
              state.project = project;
              state.isInitialized = true;
            });
          }

          // ── Load prior variant history from the backend ──────────────────
          // When the user reopens Image Studio for an image they've previously
          // edited, we reload the persisted variant entities so the VariantsStrip
          // shows up immediately with all previous AI edits available.
          if (sourceEntityId) {
            try {
              const { API_BASE_URL } = await import('../../../api/config');
              const { FILES_ENDPOINTS } = await import('../../../endpoints');
              const authToken = localStorage.getItem('authToken') || '';
              const res = await fetch(`${API_BASE_URL}/image-studio/variants/${sourceEntityId}`, {
                headers: { Authorization: `Bearer ${authToken}` },
              });
              if (res.ok) {
                const data = await res.json() as { success: boolean; variants: Array<{ entityId: string; name: string; prompt?: string; generatedAt?: string }> };
                if (data.success && data.variants.length > 0) {
                  const ops: AIOperation[] = data.variants.map((v) => ({
                    id: generateId('op'),
                    mode: 'edit' as const,
                    status: 'complete' as const,
                    prompt: v.prompt ?? 'AI Edit',
                    sourceLayerIds: [],
                    params: { applyMode: 'new-layer' as const },
                    resultVariants: [{
                      id: generateId('var'),
                      imageDataUrl: FILES_ENDPOINTS.read(v.entityId),
                      assetId: v.entityId,
                    }],
                    createdAt: v.generatedAt ?? new Date().toISOString(),
                  }));

                  set(state => {
                    // Oldest → newest; the last one becomes the active strip entry.
                    ops.forEach(op => state.aiOperations.push(op));
                    const lastOp = ops[ops.length - 1];
                    state.activeVariantsOpId = lastOp.id;
                    state.showVariantsStrip = true;
                  });

                  log.log('[imageStudioStore] loaded prior variants', { count: ops.length, sourceEntityId });
                }
              }
            } catch (err) {
              // Non-fatal — the user just won't see prior variants on reopen.
              log.log('[imageStudioStore] prior variants load failed (non-fatal)', { error: String(err) });
            }
          }
        },

        cleanup: () => {
          log.log('[imageStudioStore] cleanup', { windowId });
          // Nothing to unsubscribe from at this stage
        },

        // ── Layer actions ──────────────────────────────────────────────────
        addLayer: (type, props = {}) => {
          const id = generateId('layer');
          const layers = get().project.layers;
          const selectedId = get().selectedLayerIds[0];
          const insertIndex = selectedId
            ? layers.findIndex(l => l.id === selectedId) + 1
            : layers.length;

          const newLayer: Layer = {
            id,
            type,
            name: autoLayerName(layers, type),
            visible: true,
            locked: false,
            opacity: 1,
            blendMode: 'normal',
            transform: {
              x: 0,
              y: 0,
              width: get().project.canvas.width,
              height: get().project.canvas.height,
              rotation: 0,
            },
            ...props,
          };

          set(state => {
            state.project.layers.splice(insertIndex, 0, newLayer);
            state.selectedLayerIds = [id];
          });

          log.log('[layerActions] addLayer', { id, type, name: newLayer.name, stackPosition: insertIndex });
          return id;
        },

        removeLayer: (id) => {
          const layers = get().project.layers;
          const layer = layers.find(l => l.id === id);
          if (!layer) return;

          const prevState = JSON.stringify(layers);
          set(state => {
            state.project.layers = state.project.layers.filter(l => l.id !== id);
            state.selectedLayerIds = state.selectedLayerIds.filter(sid => sid !== id);
          });

          get().pushHistory({
            type: 'layer-delete',
            description: `Delete "${layer.name}"`,
            undo: () => {
              set(state => { state.project.layers = JSON.parse(prevState); });
            },
            redo: () => {
              set(state => { state.project.layers = state.project.layers.filter(l => l.id !== id); });
            },
          });
        },

        removeSelectedLayers: () => {
          const ids = get().selectedLayerIds;
          ids.forEach(id => get().removeLayer(id));
        },

        duplicateLayer: (id) => {
          const layers = get().project.layers;
          const original = layers.find(l => l.id === id);
          if (!original) return id;

          const newId = generateId('layer');
          const copy: Layer = {
            ...JSON.parse(JSON.stringify(original)),
            id: newId,
            name: `${original.name} copy`,
            transform: { ...original.transform, x: original.transform.x + 20, y: original.transform.y + 20 },
          };

          const idx = layers.findIndex(l => l.id === id);
          set(state => {
            state.project.layers.splice(idx + 1, 0, copy);
            state.selectedLayerIds = [newId];
          });

          log.log('[layerActions] duplicateLayer', { originalId: id, newId });
          return newId;
        },

        reorderLayers: (fromIndex, toIndex) => {
          if (fromIndex === toIndex) return;
          set(state => {
            const [layer] = state.project.layers.splice(fromIndex, 1);
            state.project.layers.splice(toIndex, 0, layer);
          });
          log.log('[layerActions] reorder', { from: fromIndex, to: toIndex });
        },

        setLayerVisibility: (id, visible) => {
          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (l) l.visible = visible;
          });
        },

        setLayerLocked: (id, locked) => {
          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (l) l.locked = locked;
          });
        },

        setLayerOpacity: (id, opacity) => {
          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (l) l.opacity = Math.max(0, Math.min(1, opacity));
          });
        },

        setLayerBlendMode: (id, blendMode) => {
          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (l) l.blendMode = blendMode;
          });
        },

        setLayerTransform: (id, transform) => {
          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (l) Object.assign(l.transform, transform);
          });
        },

        setLayerName: (id, name) => {
          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (l) l.name = name;
          });
        },

        setLayerFilters: (id, filters) => {
          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (l) l.filters = { ...l.filters, ...filters };
          });
        },

        replaceLayerContent: (id, imageData, width, height, preserveTransform) => {
          const layer = get().project.layers.find(l => l.id === id);
          if (!layer) return;

          const prevData = layer.imageData;
          const prevTransform = { ...layer.transform };

          set(state => {
            const l = state.project.layers.find(l => l.id === id);
            if (!l) return;
            l.imageData = imageData;
            if (!preserveTransform) {
              l.transform.width = width;
              l.transform.height = height;
            }
          });

          get().pushHistory({
            type: 'layer-replace',
            description: `Replace content of "${layer.name}"`,
            undo: () => {
              set(state => {
                const l = state.project.layers.find(l => l.id === id);
                if (!l) return;
                l.imageData = prevData;
                if (!preserveTransform) l.transform = prevTransform;
              });
            },
            redo: () => {
              set(state => {
                const l = state.project.layers.find(l => l.id === id);
                if (!l) return;
                l.imageData = imageData;
                if (!preserveTransform) { l.transform.width = width; l.transform.height = height; }
              });
            },
          });

          log.log('[layerActions] replaceContent', { id, preserveTransform, newSize: { width, height } });
        },

        setSelectedLayers: (ids) => {
          set(state => { state.selectedLayerIds = ids; });
        },

        toggleLayerSelection: (id) => {
          set(state => {
            const idx = state.selectedLayerIds.indexOf(id);
            if (idx >= 0) state.selectedLayerIds.splice(idx, 1);
            else state.selectedLayerIds.push(id);
          });
        },

        alignLayers: (ids, anchor, relativeTo) => {
          const { project } = get();
          const { canvas } = project;
          const targetLayers = project.layers.filter(l => ids.includes(l.id));
          if (targetLayers.length === 0) return;

          let refLeft = 0, refTop = 0, refRight = canvas.width, refBottom = canvas.height;

          if (relativeTo === 'selection' && ids.length > 1) {
            refLeft = Math.min(...targetLayers.map(l => l.transform.x));
            refTop = Math.min(...targetLayers.map(l => l.transform.y));
            refRight = Math.max(...targetLayers.map(l => l.transform.x + l.transform.width));
            refBottom = Math.max(...targetLayers.map(l => l.transform.y + l.transform.height));
          }

          set(state => {
            ids.forEach(id => {
              const l = state.project.layers.find(l => l.id === id);
              if (!l) return;
              if (anchor === 'left') l.transform.x = refLeft;
              else if (anchor === 'right') l.transform.x = refRight - l.transform.width;
              else if (anchor === 'center-h') l.transform.x = refLeft + (refRight - refLeft) / 2 - l.transform.width / 2;
              else if (anchor === 'top') l.transform.y = refTop;
              else if (anchor === 'bottom') l.transform.y = refBottom - l.transform.height;
              else if (anchor === 'center-v') l.transform.y = refTop + (refBottom - refTop) / 2 - l.transform.height / 2;
            });
          });

          log.log('[layerActions] alignLayers', { ids, anchor, relativeTo });
        },

        distributeLayers: (ids, axis) => {
          const layers = get().project.layers.filter(l => ids.includes(l.id));
          if (layers.length < 3) return;

          const sorted = [...layers].sort((a, b) =>
            axis === 'horizontal'
              ? a.transform.x - b.transform.x
              : a.transform.y - b.transform.y
          );

          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const totalSpan = axis === 'horizontal'
            ? last.transform.x + last.transform.width - first.transform.x
            : last.transform.y + last.transform.height - first.transform.y;
          const contentSize = sorted.reduce((s, l) => s + (axis === 'horizontal' ? l.transform.width : l.transform.height), 0);
          const gap = (totalSpan - contentSize) / (sorted.length - 1);

          set(state => {
            let cursor = axis === 'horizontal' ? first.transform.x : first.transform.y;
            sorted.forEach(sl => {
              const l = state.project.layers.find(l => l.id === sl.id);
              if (!l) return;
              if (axis === 'horizontal') { l.transform.x = cursor; cursor += l.transform.width + gap; }
              else { l.transform.y = cursor; cursor += l.transform.height + gap; }
            });
          });

          log.log('[layerActions] distributeLayers', { ids, axis });
        },

        mergeLayersDown: (id) => {
          const layers = get().project.layers;
          const idx = layers.findIndex(l => l.id === id);
          if (idx <= 0) return;
          // Actual pixel merge is deferred to the compositor
          // For now remove the lower layer and rename upper
          get().removeLayer(layers[idx - 1].id);
          log.log('[layerActions] mergeDown', { id, belowId: layers[idx - 1].id });
        },

        // ── Selection actions ──────────────────────────────────────────────
        setSelection: (type, bounds) => {
          set(state => {
            state.selection.type = type;
            state.selection.bounds = bounds;
          });
        },

        setSelectionMode: (mode) => {
          set(state => { state.selection.mode = mode; });
        },

        clearSelection: () => {
          set(state => {
            state.selection = DEFAULT_SELECTION();
            state.activeMaskData = null;
          });
        },

        invertSelection: () => {
          set(state => { state.selection.maskSerial++; });
        },

        selectAll: () => {
          const { canvas } = get().project;
          set(state => {
            state.selection.type = 'all';
            state.selection.bounds = { x: 0, y: 0, width: canvas.width, height: canvas.height };
            state.selection.maskSerial++;
          });
        },

        bumpMaskSerial: () => {
          set(state => { state.selection.maskSerial++; });
        },

        setActiveMaskData: (data) => {
          set(state => { state.activeMaskData = data; });
          log.log('[imageStudioStore] activeMaskData set', { hasData: data !== null, length: data?.length ?? 0 });
        },

        receiveAgentProgress: (opId, prompt, mode = 'edit') => {
          set(state => {
            // De-dupe: if the op already exists (double-broadcast), leave it alone.
            if (state.aiOperations.some(o => o.id === opId)) return;
            const op: AIOperation = {
              id: opId,
              mode,
              status: 'running',
              prompt,
              sourceLayerIds: [...state.selectedLayerIds],
              params: { applyMode: mode === 'generate' ? 'new-layer' : 'new-layer' },
              resultVariants: [],
              createdAt: new Date().toISOString(),
            };
            state.aiOperations.push(op);
            state.activeVariantsOpId = opId;
            state.showVariantsStrip = true;
          });
          log.log('[imageStudioStore] agentProgress', { opId, prompt, mode });
        },

        receiveAgentEditResult: (opId, variants, prompt, mode = 'edit') => {
          const finalOpId = opId ?? generateId('op');
          const variantResults: VariantResult[] = variants.map((v, i) => ({
            id: `var_${Date.now()}_${i}`,
            imageDataUrl: v.imageUrl,
            assetId: v.entityId,
          }));

          set(state => {
            const existing = state.aiOperations.find(o => o.id === finalOpId);
            if (existing) {
              existing.resultVariants = variantResults;
              existing.status = 'complete';
              existing.durationMs = Date.now() - new Date(existing.createdAt).getTime();
              existing.prompt = existing.prompt || prompt;
            } else {
              const op: AIOperation = {
                id: finalOpId,
                mode,
                status: 'complete',
                prompt,
                sourceLayerIds: [...state.selectedLayerIds],
                params: { applyMode: mode === 'generate' ? 'new-layer' : 'new-layer' },
                resultVariants: variantResults,
                createdAt: new Date().toISOString(),
              };
              state.aiOperations.push(op);
            }
            state.activeOperationId = null;
            state.activeVariantsOpId = finalOpId;
            state.showVariantsStrip = true;
          });

          log.log('[imageStudioStore] agentEditResult', { opId: finalOpId, variantCount: variants.length, prompt, mode });
        },

        receiveAgentFailure: (opId, error) => {
          set(state => {
            const op = state.aiOperations.find(o => o.id === opId);
            if (op) {
              op.status = 'failed';
              op.error = error;
              op.durationMs = Date.now() - new Date(op.createdAt).getTime();
            }
            // If this was the active spinner, hide the strip so the user isn't stuck.
            if (state.activeVariantsOpId === opId) {
              state.showVariantsStrip = false;
              state.activeVariantsOpId = null;
            }
          });
          log.log('[imageStudioStore] agentFailure', { opId, error });
        },

        // ── Canvas / zoom ──────────────────────────────────────────────────
        setViewTransform: (t) => {
          set(state => { Object.assign(state.viewTransform, t); });
        },

        fitToScreen: (containerWidth, containerHeight) => {
          const { canvas } = get().project;
          const scaleX = (containerWidth * 0.9) / canvas.width;
          const scaleY = (containerHeight * 0.9) / canvas.height;
          const scale = Math.min(scaleX, scaleY);
          const offsetX = (containerWidth - canvas.width * scale) / 2;
          const offsetY = (containerHeight - canvas.height * scale) / 2;
          set(state => { state.viewTransform = { offsetX, offsetY, scale }; });
          log.log('[PanZoomController] fitToScreen', { scale, offsetX, offsetY, containerWidth, containerHeight });
        },

        zoomTo100: () => {
          const vt = get().viewTransform;
          // Keep the canvas centered at current offset
          set(state => {
            state.viewTransform = { offsetX: vt.offsetX, offsetY: vt.offsetY, scale: 1 };
          });
          log.log('[PanZoomController] zoomTo100', { trigger: 'zoomTo100' });
        },

        zoomToSelection: (containerWidth, containerHeight) => {
          const bounds = get().selection.bounds;
          if (!bounds) { get().fitToScreen(containerWidth, containerHeight); return; }
          const padding = 0.8;
          const scaleX = (containerWidth * padding) / bounds.width;
          const scaleY = (containerHeight * padding) / bounds.height;
          const scale = Math.min(scaleX, scaleY);
          const offsetX = containerWidth / 2 - (bounds.x + bounds.width / 2) * scale;
          const offsetY = containerHeight / 2 - (bounds.y + bounds.height / 2) * scale;
          set(state => { state.viewTransform = { offsetX, offsetY, scale }; });
          log.log('[PanZoomController] zoomToSelection', { bounds, scale });
        },

        setCanvasSize: (width, height) => {
          set(state => { state.project.canvas.width = width; state.project.canvas.height = height; });
        },

        setCanvasBackground: (background) => {
          set(state => { state.project.canvas.background = background; });
        },

        // ── Tool / brush ───────────────────────────────────────────────────
        setActiveTool: (tool) => {
          const prev = get().activeTool;
          set(state => { state.activeTool = tool; });
          log.log('[ToolBar] toolSwitch', { from: prev, to: tool });
        },

        setBrushSettings: (settings) => {
          set(state => { Object.assign(state.brushSettings, settings); });
        },

        // ── AI operations ──────────────────────────────────────────────────
        createAIOperation: (mode, prompt, opts = {}) => {
          const op: AIOperation = {
            id: generateId('op'),
            mode,
            status: 'pending',
            prompt,
            sourceLayerIds: opts.sourceLayerIds ?? get().selectedLayerIds,
            selectionMaskData: opts.selectionMaskData,
            transparentBackground: opts.transparentBackground ?? false,
            referenceImages: opts.referenceImages ?? [],
            params: {
              qualityMode: 'standard',
              variantCount: 4,
              applyMode: mode === 'generate' ? 'new-layer' : 'replace-layer',
              ...opts.params,
            },
            resultVariants: [],
            createdAt: new Date().toISOString(),
          };

          set(state => {
            state.aiOperations.push(op);
            state.activeOperationId = op.id;
          });

          log.log('[aiOperationService] createOperation', {
            opId: op.id, mode, prompt,
            hasSelection: !!op.selectionMaskData,
            referenceCount: op.referenceImages?.length ?? 0,
            transparentBg: op.transparentBackground,
          });
          return op;
        },

        updateAIOperation: (id, updates) => {
          set(state => {
            const op = state.aiOperations.find(o => o.id === id);
            if (op) Object.assign(op, updates);
          });
        },

        receiveAIResults: (opId, variants) => {
          set(state => {
            const op = state.aiOperations.find(o => o.id === opId);
            if (!op) return;
            op.resultVariants = variants;
            op.status = 'complete';
            op.durationMs = Date.now() - new Date(op.createdAt).getTime();
            state.showVariantsStrip = true;
            state.activeVariantsOpId = opId;
            state.activeOperationId = null;
          });
          log.log('[aiOperationService] operationComplete', { opId, variantCount: variants.length });
        },

        cancelAIOperation: (opId) => {
          set(state => {
            const op = state.aiOperations.find(o => o.id === opId);
            if (op) { op.status = 'cancelled'; }
            if (state.activeOperationId === opId) state.activeOperationId = null;
          });
          log.log('[aiOperationService] cancel', { opId });
        },

        acceptAIVariant: (opId, variantIndex, applyMode) => {
          const op = get().aiOperations.find(o => o.id === opId);
          if (!op) return null;
          const variant = op.resultVariants[variantIndex];
          if (!variant) return null;

          let newLayerId: string | null = null;

          if (applyMode === 'replace-layer') {
            const targetId = op.sourceLayerIds[0];
            const target = get().project.layers.find(l => l.id === targetId);
            if (target) {
              get().replaceLayerContent(targetId, variant.imageDataUrl, target.transform.width, target.transform.height, true);
              newLayerId = targetId;
            }
          } else {
            // new-layer or branch — create new layer
            newLayerId = get().addLayer('generated', {
              imageData: variant.imageDataUrl,
              aiOperationId: opId,
            });
          }

          set(state => {
            const op = state.aiOperations.find(o => o.id === opId);
            if (op) { op.acceptedVariantIndex = variantIndex; }
            state.showVariantsStrip = false;
            state.activeVariantsOpId = null;
            // Track the URL so MediaViewerApp can display the latest edit
            // when the user exits studio mode.
            state.latestExportUrl = variant.imageDataUrl;
          });

          log.log('[aiActions] acceptVariant', { opId, variantIndex, applyMode, newLayerId, exportUrl: variant.imageDataUrl.slice(0, 80) });
          return newLayerId;
        },

        rejectAIVariants: (_opId) => {
          set(state => {
            state.showVariantsStrip = false;
            state.activeVariantsOpId = null;
          });
        },

        setActiveVariantsOp: (opId) => {
          set(state => {
            state.activeVariantsOpId = opId;
            state.showVariantsStrip = opId !== null;
          });
        },

        reopenVariantsStrip: () => {
          const completed = get().aiOperations.filter(
            o => o.status === 'complete' && o.resultVariants.length > 0,
          );
          if (completed.length === 0) return false;
          const mostRecent = completed[completed.length - 1];
          set(state => {
            state.activeVariantsOpId = mostRecent.id;
            state.showVariantsStrip = true;
          });
          return true;
        },

        stepVariantsOp: (direction) => {
          const { aiOperations, activeVariantsOpId } = get();
          // Only cycle through completed ops with at least one variant — pending/failed
          // rows would give us an empty strip which is worse than just skipping them.
          const completed = aiOperations.filter(
            o => o.status === 'complete' && o.resultVariants.length > 0,
          );
          if (completed.length === 0) return;
          const currentIdx = activeVariantsOpId
            ? completed.findIndex(o => o.id === activeVariantsOpId)
            : -1;
          let nextIdx: number;
          if (currentIdx < 0) {
            nextIdx = direction === 'next' ? 0 : completed.length - 1;
          } else if (direction === 'prev') {
            nextIdx = Math.max(0, currentIdx - 1);
          } else {
            nextIdx = Math.min(completed.length - 1, currentIdx + 1);
          }
          const target = completed[nextIdx];
          set(state => {
            state.activeVariantsOpId = target.id;
            state.showVariantsStrip = true;
          });
        },

        // ── History ────────────────────────────────────────────────────────
        pushHistory: (entry) => {
          const id = generateId('h');
          const full: HistoryEntry = {
            ...entry,
            id,
            timestamp: Date.now(),
            branchId: get().currentBranchId,
          };

          set(state => {
            // Trim redo branch when a new action is taken
            state.historyStack = state.historyStack.slice(0, state.historyIndex + 1);
            state.historyStack.push(full);
            state.historyIndex = state.historyStack.length - 1;
          });

          log.log('[historyActions] push', { entryId: id, type: entry.type, description: entry.description, stackSize: get().historyStack.length });
        },

        undo: () => {
          const { historyIndex, historyStack } = get();
          if (historyIndex < 0) return;
          const entry = historyStack[historyIndex];
          entry.undo?.();
          set(state => { state.historyIndex--; });
          log.log('[historyActions] undo', { entryId: entry.id });
        },

        redo: () => {
          const { historyIndex, historyStack } = get();
          if (historyIndex >= historyStack.length - 1) return;
          const entry = historyStack[historyIndex + 1];
          entry.redo?.();
          set(state => { state.historyIndex++; });
          log.log('[historyActions] redo', { entryId: entry.id });
        },

        canUndo: () => get().historyIndex >= 0,
        canRedo: () => get().historyIndex < get().historyStack.length - 1,

        // ── Style recipes ──────────────────────────────────────────────────
        saveStyleRecipe: (recipe) => {
          const id = generateId('style');
          set(state => { state.project.savedStyles.push({ ...recipe, id }); });
        },

        deleteStyleRecipe: (id) => {
          set(state => { state.project.savedStyles = state.project.savedStyles.filter(s => s.id !== id); });
        },

        // ── UI ─────────────────────────────────────────────────────────────
        setShowLayerPanel: (show) => { set(state => { state.showLayerPanel = show; }); },
        setShowInspector: (show) => { set(state => { state.showInspector = show; }); },
        setShowHistoryPanel: (show) => { set(state => { state.showHistoryPanel = show; }); },

        // ── Persistence ────────────────────────────────────────────────────
        setIsSaving: (saving) => { set(state => { state.isSaving = saving; }); },
        updateProjectMeta: (updates) => {
          set(state => { Object.assign(state.project, updates); });
        },
      }))
    )
  );
};

// ─── Public API ────────────────────────────────────────────────────────────

export type ImageStudioStoreInstance = StoreApi<ImageStudioStore>;

export function getImageStudioStore(windowId: string, spaceId: string): ReturnType<typeof createImageStudioStore> {
  const key = `${spaceId}:${windowId}`;
  if (!imageStudioStores.has(key)) {
    log.log('[imageStudioStore] creating new store', { windowId, spaceId });
    imageStudioStores.set(key, createImageStudioStore(windowId, spaceId));
  }
  return imageStudioStores.get(key)!;
}

export function cleanupImageStudioStore(windowId: string, spaceId: string): void {
  const key = `${spaceId}:${windowId}`;
  const store = imageStudioStores.get(key);
  if (store) {
    store.getState().cleanup();
    imageStudioStores.delete(key);
    log.log('[imageStudioStore] cleaned up', { windowId, spaceId });
  }
}

export function useImageStudioStore(windowId: string, spaceId: string) {
  return getImageStudioStore(windowId, spaceId);
}

/**
 * Fan out an `image_studio_result` broadcast to every Image Studio window
 * open in the given space. Called from the global command handler.
 */
export function pushAgentResult(
  spaceId: string,
  opId: string | null,
  variants: Array<{ imageUrl: string; entityId?: string; name?: string }>,
  prompt: string,
  mode: AIOperationMode = 'edit',
): number {
  let count = 0;
  for (const [key, store] of imageStudioStores) {
    if (key.startsWith(`${spaceId}:`)) {
      store.getState().receiveAgentEditResult(opId, variants, prompt, mode);
      count++;
    }
  }
  log.log('[imageStudioStore] pushed agent result to stores', { spaceId, count, variantCount: variants.length });
  return count;
}

/** Fan out an `image_studio_progress` (running) broadcast so every open
 *  Image Studio in this space shows a pending placeholder. */
export function pushAgentProgress(
  spaceId: string,
  opId: string,
  prompt: string,
  mode: AIOperationMode = 'edit',
): number {
  let count = 0;
  for (const [key, store] of imageStudioStores) {
    if (key.startsWith(`${spaceId}:`)) {
      store.getState().receiveAgentProgress(opId, prompt, mode);
      count++;
    }
  }
  log.log('[imageStudioStore] pushed agent progress to stores', { spaceId, opId, count });
  return count;
}

/** Fan out a failure broadcast so the pending card clears. */
export function pushAgentFailure(spaceId: string, opId: string, error: string): number {
  let count = 0;
  for (const [key, store] of imageStudioStores) {
    if (key.startsWith(`${spaceId}:`)) {
      store.getState().receiveAgentFailure(opId, error);
      count++;
    }
  }
  log.log('[imageStudioStore] pushed agent failure to stores', { spaceId, opId, count });
  return count;
}
